import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { fetchCurrentUserInfo, fetchFullProfile } from "../script/rpc";
import "./applayout.css";


/* ─────────────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────────────── */
const Icons = {
  dashboard: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  tree: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" />
      <path d="M12 6v6M12 12l-5.5 5M12 12l5.5 5" />
    </svg>
  ),
  transfer: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
    </svg>
  ),
  user: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  logout: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  edit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  menu: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  ),
  close: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  team: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  commissions: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  medal: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

function initials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
}

/* Navigation items — path matches the route */
const NAV_ITEMS = [
  { icon: Icons.dashboard, label: "Dashboard", path: "/dashboard", matchPaths: ["/dashboard"] },
  { icon: Icons.tree, label: "Quantobuzz Tree", path: "/tree", matchPaths: ["/tree"] },
  { icon: Icons.transfer, label: "Transactions", path: "/transactions", matchPaths: ["/transactions", "/tx_new"] },
  { icon: Icons.team, label: "Team Members", path: "/team", matchPaths: ["/team"] },
  { icon: Icons.commissions, label: "Commissions", path: "/commissions", matchPaths: ["/commissions"] },
  { icon: Icons.medal, label: "House Reward", path: "/incentives", matchPaths: ["/incentives"] },
  { icon: Icons.user, label: "My Profile", path: "/profile", matchPaths: ["/profile"] },
];

export default function AppLayout() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleNavClick = (path) => {
    if (location.pathname === path) {
      // Don't reload the entire page, just close sidebar
    } else {
      navigate(path);
    }
    setSidebarOpen(false);
  };

  // Close the mobile drawer automatically if the viewport grows past
  // the mobile breakpoint while it's open (e.g. rotating a tablet,
  // resizing a desktop browser window).
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { uid, password } = auth;
        const info = await fetchCurrentUserInfo(uid, password);
        if (info && alive) {
          setLoginEmail(info.email || "");
          if (info.partner_id) {
            const prof = await fetchFullProfile(uid, password, info.partner_id[0]);
            if (alive) setProfile(prof);
          }
        }
      } catch (err) {
        console.error("Sidebar load failed:", err);
      }
    })();
    return () => { alive = false; };
  }, [auth]);

  const userName = profile?.name || "";
  const userEmail = loginEmail || profile?.email || "";

  return (
    <div className="dp-root">
      {sidebarOpen && <div className="dp-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ══ SIDEBAR ══ */}
      <aside className={`dp-sidebar${sidebarOpen ? " dp-sidebar--open" : ""}`}>
        {/* Brand */}
        <div className="dp-brand">
          <div className="dp-brand-logo">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="4" r="2.4" fill="white" />
              <circle cx="4" cy="16" r="2.4" fill="rgba(255,255,255,0.7)" />
              <circle cx="16" cy="16" r="2.4" fill="rgba(255,255,255,0.7)" />
              <line x1="10" y1="6" x2="4" y2="14" stroke="white" strokeWidth="1.5" />
              <line x1="10" y1="6" x2="16" y2="14" stroke="white" strokeWidth="1.5" />
            </svg>
          </div>
          <span className="dp-brand-name">Quantobuzz</span>
          <button className="dp-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close menu">{Icons.close}</button>
        </div>

        {/* User Profile */}
        <div className="dp-user-card">
          <div className="dp-avatar">{initials(userName)}</div>
          <div className="dp-user-info">
            <div className="dp-user-name">{userName}</div>
            <div className="dp-user-meta">{userEmail}</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="dp-nav">
          <div className="dp-nav-label">Main Menu</div>
          {NAV_ITEMS.map((item) => {
            const isActive = item.matchPaths.some(
              (p) => location.pathname === p || location.pathname.startsWith(p + "/")
            );
            return (
              <button
                key={item.path}
                className={`dp-nav-item${isActive ? " dp-nav-item--active" : ""}`}
                onClick={() => handleNavClick(item.path)}
              >
                <span className="dp-nav-icon">{item.icon}</span>
                <span className="dp-nav-text">{item.label}</span>
                {isActive && <span className="dp-nav-dot" />}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="dp-sidebar-footer">
          <button className="dp-nav-item dp-nav-item--logout" onClick={handleLogout}>
            <span className="dp-nav-icon">{Icons.logout}</span>
            <span className="dp-nav-text">Sign Out</span>
          </button>
          <div className="dp-powered">
            <a href="https://xamta.co/" target="_blank" rel="noopener noreferrer" className="dp-powered-link">
              Powered by XAMTA © {new Date().getFullYear()}
            </a>
          </div>
        </div>
      </aside>

      {/* ══ CONTENT AREA — each child route renders here ══ */}
      <div className="dp-content">
        {/* Mobile topbar */}
        <div className="dp-mobile-header">
          <button className="dp-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">{Icons.menu}</button>
          <div className="dp-mobile-brand">
            <div className="dp-brand-logo" style={{ width: 28, height: 28 }}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="4" r="2.4" fill="white" />
                <circle cx="4" cy="16" r="2.4" fill="rgba(255,255,255,0.7)" />
                <circle cx="16" cy="16" r="2.4" fill="rgba(255,255,255,0.7)" />
                <line x1="10" y1="6" x2="4" y2="14" stroke="white" strokeWidth="1.5" />
                <line x1="10" y1="6" x2="16" y2="14" stroke="white" strokeWidth="1.5" />
              </svg>
            </div>
            <span className="dp-brand-name">Quantobuzz</span>
          </div>
          <div className="dp-mobile-avatar">{initials(userName)}</div>
        </div>

        {/* Route content */}
        <Outlet />
      </div>
    </div>
  );
}